import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Calendar, Video, Clock, ChevronDown, Lock, PlayCircle, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { formatEventTime, minutesUntil, isJoinable, isEasternTimezone } from "../lib/datetime";
import { googleCalendarUrl, outlookCalendarUrl, icsUrl } from "../lib/calendar";
import { thumbnailFor, defaultThumbnailFor } from "../lib/eventThumbnail";
import { detectMeetingProvider } from "../lib/meetingProvider";

/**
 * Phase 13.3 — Compact horizontal live event cards. Used on both the public
 * homepage and the authed dashboard. Replays render below upcoming.
 */

const AUDIENCE_TONE = {
  automotive: "border-[#FF4343]/50 text-[#FFB6B6]",
  business: "border-[#3B82F6]/50 text-[#B6CDFF]",
  entrepreneur: "border-[#10B981]/50 text-[#A8E8CE]",
  all: "border-gray-200 text-gray-600",
};

function relativeChip(utcIso) {
  if (!utcIso) return null;
  const m = minutesUntil(utcIso);
  if (m < -1) return null;
  if (m <= 10 && m >= -60) return { text: "LIVE", live: true };
  if (m < 60) return { text: `IN ${Math.max(1, Math.round(m))} MIN` };
  if (m < 24 * 60) {
    const hrs = Math.round(m / 60);
    return { text: hrs === 1 ? "IN 1 HOUR" : `IN ${hrs} HOURS` };
  }
  const dt = new Date(utcIso);
  const today = new Date();
  const sameWeek = (dt - today) / (1000 * 60 * 60 * 24) < 7;
  if (sameWeek) {
    const day = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(dt).toUpperCase();
    if (m < 48 * 60) return { text: "TOMORROW" };
    return { text: day };
  }
  const short = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(dt).toUpperCase();
  return { text: short };
}

function initials(name) {
  if (!name) return "F";
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export default function UpcomingEvents({ audience, limit = 3, authed = false, variant = "public" }) {
  const [events, setEvents] = useState(null);
  const [replays, setReplays] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (audience && audience !== "all") params.set("audience", audience);
    params.set("limit", String(limit));
    api
      .get(`/live-events/upcoming?${params.toString()}`)
      .then((r) => setEvents(r.data.events || []))
      .catch((e) => { console.error("upcoming events load failed", e); setEvents([]); });

    if (authed) {
      const rp = new URLSearchParams();
      if (audience && audience !== "all") rp.set("audience", audience);
      rp.set("limit", "6");
      api
        .get(`/live-events/replays?${rp.toString()}`)
        .then((r) => setReplays(r.data.events || []))
        .catch(() => setReplays([]));
    }
  }, [audience, limit, authed]);

  if (events === null) {
    return (
      <div className="text-gray-500 text-sm" data-testid="upcoming-events-loading">
        Loading upcoming events…
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {events.length === 0 ? (
        <div className="border border-amber-400/30 bg-amber-500/5 rounded-2xl p-8 text-center" data-testid="upcoming-events-empty">
          <AlertCircle size={20} className="text-amber-300 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">No upcoming live events scheduled. Check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5" data-testid="upcoming-events-list">
          {events.map((ev) => (
            <EventCard key={ev.id} event={ev} authed={authed} />
          ))}
        </div>
      )}

      {authed && replays.length > 0 && (
        <section data-testid="dashboard-replays">
          <div className="flex items-end justify-between mb-4">
            <h3 className="text-base sm:text-lg font-black tracking-tight text-gray-700 flex items-center gap-2">
              <PlayCircle size={16} className="text-amber-300" /> Replays
            </h3>
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400">
              {replays.length} {replays.length === 1 ? "session" : "sessions"}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {replays.map((ev) => (
              <EventCard key={ev.id} event={ev} authed={authed} replay />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function EventCard({ event, authed, replay = false }) {
  const t = formatEventTime(event.starts_at_utc);
  const joinable = !replay && isJoinable(event.starts_at_utc);
  const chip = replay ? { text: "RECORDING", muted: true } : relativeChip(event.starts_at_utc);
  const inEastern = isEasternTimezone();
  const thumb = thumbnailFor(event);

  return (
    <article
      className="group bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col sm:flex-row transition-all duration-200 hover:border-amber-400/40 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-black/30"
      data-testid={`event-card-${event.id}`}
    >
      {/* Thumbnail — full-width banner on mobile, fixed-width left rail on sm+ */}
      <div className="relative w-full sm:w-[180px] lg:w-[220px] sm:flex-shrink-0 aspect-[16/9] sm:aspect-auto bg-fc-navy overflow-hidden">
        <img
          src={thumb}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          loading="lazy"
          onError={(e) => { e.currentTarget.src = defaultThumbnailFor(event.id); }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-fc-dark-navy via-fc-dark-navy/40 to-transparent sm:bg-gradient-to-r sm:from-transparent sm:via-transparent sm:to-fc-navy/10" />
      </div>

      {/* Right body */}
      <div className="flex-1 min-w-0 p-4 sm:p-5 lg:p-6 flex flex-col">
        {/* Top row: relative chip + audience chips */}
        <div className="flex items-start justify-between gap-2 mb-2">
          {chip && (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[9px] tracking-[0.2em] uppercase font-black rounded-full ${
                chip.live ? "bg-fc-red text-white"
                : chip.muted ? "bg-gray-100 text-gray-700 border border-gray-200"
                : "bg-amber-500/95 text-fc-navy"
              }`}
              data-testid={`event-chip-${event.id}`}
            >
              {chip.live && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
              <span className="whitespace-nowrap">{chip.text}</span>
            </span>
          )}
          <div className="flex flex-wrap gap-1 justify-end">
            {(event.audiences || []).map((a) => (
              <span
                key={a}
                className={`whitespace-nowrap px-1.5 py-0.5 text-[9px] uppercase font-bold tracking-wider border rounded ${AUDIENCE_TONE[a] || AUDIENCE_TONE.all}`}
              >
                {a}
              </span>
            ))}
          </div>
        </div>

        {/* Title */}
        <h3 className="font-black text-base sm:text-lg leading-snug text-gray-900 line-clamp-2 mb-2" data-testid={`event-title-${event.id}`}>
          {event.title}
        </h3>

        {/* Host row */}
        {event.host_name && (
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center text-[8px] font-black text-amber-700">
              {initials(event.host_name)}
            </div>
            <p className="text-xs text-gray-700 truncate">
              <span className="font-semibold">{event.host_name}</span>
              {event.host_title && <span className="text-gray-500"> · {event.host_title}</span>}
            </p>
          </div>
        )}

        {/* Time line — single line if user in Eastern, two lines otherwise */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-start gap-1.5 min-w-0">
            <Clock size={12} className="text-amber-600 flex-shrink-0 mt-[3px]" />
            <div className="min-w-0 leading-tight">
              <p className="text-xs font-bold text-gray-900 truncate">{t.local}</p>
              {!inEastern && (
                <p className="text-[10px] text-gray-500 truncate">({t.et} ET)</p>
              )}
            </div>
          </div>
          {(() => {
            const prov = detectMeetingProvider(event.zoom_url);
            return prov ? (
              <span
                className={`text-[9px] tracking-[0.15em] font-bold uppercase px-2 py-0.5 rounded-full border flex-shrink-0 ${prov.color}`}
                data-testid={`event-provider-${event.id}`}
                title={`Streamed via ${prov.label}`}
              >
                {prov.label}
              </span>
            ) : null;
          })()}
        </div>

        {/* Description preview (line-clamp-1, optional) */}
        {event.description && (
          <p className="text-[11px] text-gray-600 leading-snug line-clamp-1 mb-3">
            {event.description}
          </p>
        )}

        {/* Actions row pinned to bottom */}
        <div className="mt-auto flex flex-nowrap items-center justify-between gap-2 pt-2">
          {replay ? (
            event.recording_url ? (
              <a
                href={event.recording_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black tracking-[0.18em] uppercase bg-amber-400 hover:bg-amber-300 text-fc-navy rounded transition-colors"
                data-testid={`event-watch-${event.id}`}
              >
                <PlayCircle size={12} /> Watch Replay
              </a>
            ) : (
              <span className="text-gray-400 text-[11px]">Replay coming soon</span>
            )
          ) : authed ? (
            event.zoom_url ? (
              <a
                href={joinable ? event.zoom_url : undefined}
                onClick={!joinable ? (e) => e.preventDefault() : undefined}
                target="_blank"
                rel="noopener noreferrer"
                title={joinable ? "Join the Zoom session" : "Available 10 min before start"}
                aria-disabled={!joinable}
                className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black tracking-[0.18em] uppercase rounded transition-colors ${
                  joinable
                    ? "bg-amber-400 hover:bg-amber-300 text-fc-navy"
                    : "border border-gray-300 text-gray-500 hover:border-gray-300 hover:text-gray-600 cursor-not-allowed"
                }`}
                data-testid={`event-join-${event.id}`}
              >
                <Video size={12} /> {joinable ? "Join Now" : "Join Meeting"}
              </a>
            ) : (
              <span className="text-gray-400 text-[11px]">Zoom link coming soon</span>
            )
          ) : (
            <Link
              to="/register"
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black tracking-[0.18em] uppercase bg-fc-red hover:bg-fc-red/90 text-white rounded transition-colors"
              data-testid={`event-signup-${event.id}`}
            >
              {event.zoom_url_locked && <Lock size={10} />} Reserve Your Seat
            </Link>
          )}
          {!replay && <AddToCalendar event={event} />}
        </div>
      </div>
    </article>
  );
}

function AddToCalendar({ event }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-bold tracking-[0.18em] uppercase border border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-colors rounded"
        data-testid={`add-to-calendar-${event.id}`}
      >
        <Calendar size={11} /> Add <ChevronDown size={10} />
      </button>
      {open && (
        <div
          className="absolute right-0 bottom-full mb-2 bg-white border border-gray-200 rounded min-w-[180px] py-1 z-30 shadow-xl shadow-black/40"
          data-testid={`add-to-calendar-menu-${event.id}`}
        >
          <a href={googleCalendarUrl(event)} target="_blank" rel="noopener noreferrer"
             className="block px-4 py-2 text-xs hover:bg-gray-100 transition-colors">Google Calendar</a>
          <a href={outlookCalendarUrl(event)} target="_blank" rel="noopener noreferrer"
             className="block px-4 py-2 text-xs hover:bg-gray-100 transition-colors">Outlook Web</a>
          <a href={icsUrl(event)} download
             className="block px-4 py-2 text-xs hover:bg-gray-100 transition-colors">Apple / .ics file</a>
        </div>
      )}
    </div>
  );
}
